import { Loader2 } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  useCheckPhoneVerification,
  usePhoneVerificationStatus,
  useStartPhoneVerification,
} from "@/features/customer/hooks/use-phone-verification";

export default function PhoneVerifyPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const next = searchParams.get("next");

  const status = usePhoneVerificationStatus();
  const startMutation = useStartPhoneVerification();
  const checkMutation = useCheckPhoneVerification();

  const [phone, setPhone] = useState("");
  const [code, setCode] = useState("");
  const [step, setStep] = useState<"phone" | "code">("phone");

  if (status.isPending) {
    return (
      <div className="flex justify-center py-10">
        <Loader2 className="h-6 w-6 animate-spin" />
      </div>
    );
  }

  if (status.data?.verified) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>{t("phoneVerification.title", "Verificar teléfono")}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <p>
            {t("phoneVerification.alreadyVerified", "Tu teléfono ya está verificado")}
            {": "}
            <span className="font-medium">{status.data.phone}</span>
          </p>
          <Button onClick={() => navigate(next ?? "/account")}>{t("common.back", "Volver")}</Button>
        </CardContent>
      </Card>
    );
  }

  const handleSendCode = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await startMutation.mutateAsync(phone.trim());
      setStep("code");
      toast.success(t("phoneVerification.sentCode", "Te enviamos un código por SMS"));
    } catch (error) {
      toast.error((error as Error).message);
    }
  };

  const handleVerify = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await checkMutation.mutateAsync(code.trim());
      toast.success(t("phoneVerification.success", "Teléfono verificado"));
      navigate(next ?? "/account");
    } catch (error) {
      toast.error((error as Error).message);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t("phoneVerification.title", "Verificar teléfono")}</CardTitle>
      </CardHeader>
      <CardContent>
        <p className="mb-4 text-sm text-muted-foreground">
          {t(
            "phoneVerification.description",
            "Te enviaremos un código por SMS para verificar tu número.",
          )}
        </p>

        {step === "phone" ? (
          <form onSubmit={handleSendCode} className="space-y-4">
            <div className="space-y-1">
              <Label htmlFor="phone">
                {t("phoneVerification.phoneLabel", "Número de teléfono")}
              </Label>
              <Input
                id="phone"
                type="tel"
                placeholder={t("phoneVerification.phonePlaceholder", "+59899...")}
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                required
                pattern="^\+[1-9]\d{6,14}$"
              />
            </div>
            <Button type="submit" disabled={startMutation.isPending}>
              {startMutation.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                t("phoneVerification.sendCode", "Enviar código")
              )}
            </Button>
          </form>
        ) : (
          <form onSubmit={handleVerify} className="space-y-4">
            <p className="text-sm">
              {t("phoneVerification.codeSentTo", "Código enviado a")}{" "}
              <span className="font-medium">{phone}</span>
            </p>
            <div className="space-y-1">
              <Label htmlFor="code">
                {t("phoneVerification.codeLabel", "Código de verificación")}
              </Label>
              <Input
                id="code"
                inputMode="numeric"
                autoComplete="one-time-code"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                maxLength={10}
                required
              />
            </div>
            <div className="flex gap-2">
              <Button type="button" variant="outline" onClick={() => setStep("phone")}>
                {t("common.back", "Volver")}
              </Button>
              <Button type="submit" disabled={checkMutation.isPending}>
                {checkMutation.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  t("phoneVerification.verify", "Verificar")
                )}
              </Button>
            </div>
          </form>
        )}
      </CardContent>
    </Card>
  );
}
